import type { LocaleCode } from "@homeinstead/locale";

import {
  getPostalCodeFormatErrorMessage,
  getPostalCodeRequiredErrorMessage,
  getPostalLocationRequiredErrorMessage,
  isPostalCodeFormatValidForLocale,
} from "@homeinstead/locale";

type PostalLocationValues = {
  postalCode: string;
  location: string;
};

/**
 * True when the typed value matches the active locale's postal code format
 * (US ZIP, Canadian postal code, or Eircode). Mirrors the format check used
 * by non-LFB office search steps so offices are only fetched once the typed
 * value could plausibly resolve to a location.
 */
function isPostalCodeFormatValid(
  value: string,
  localeCode: LocaleCode,
): boolean {
  return isPostalCodeFormatValidForLocale(value.trim(), localeCode);
}

/**
 * Returns validation errors for the postal step when values are incomplete or invalid.
 * - postalCode "required" error when user has not typed anything (location is empty).
 * - postalCode "format" error when the typed value does not match a valid postal code.
 * - location error when a valid zip is typed but no office has been selected (postalCode empty).
 */
function getPostalLocationValidationErrors(
  values: PostalLocationValues,
  localeCode: LocaleCode,
): Record<string, string[]> {
  const validationErrors: Record<string, string[]> = {};
  const typedPostalCode = values.location.trim();
  const hasTypedPostalCode = typedPostalCode !== "";
  const hasSelectedLocation = values.postalCode.trim() !== "";

  if (!hasTypedPostalCode) {
    validationErrors.postalCode = [
      getPostalCodeRequiredErrorMessage(localeCode),
    ];
    return validationErrors;
  }

  if (!isPostalCodeFormatValid(typedPostalCode, localeCode)) {
    validationErrors.postalCode = [getPostalCodeFormatErrorMessage(localeCode)];
    return validationErrors;
  }

  if (!hasSelectedLocation) {
    validationErrors.location = [
      getPostalLocationRequiredErrorMessage(localeCode),
    ];
  }

  return validationErrors;
}

export { getPostalLocationValidationErrors, isPostalCodeFormatValid };
